'use client';

import {
  ReactNode,
  useState,
  FormHTMLAttributes,
  FormEventHandler,
  useRef,
  FormEvent,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { twMerge } from 'tailwind-merge';
import {
  FriendlyCaptcha,
  FriendlyCaptchaProps,
  FriendlyCaptchaRef,
} from '../FriendlyCaptcha';
import { HeaderSection, type HeaderSectionProps } from '../HeaderSection';
import type { DataAttributes } from '../../../utils';
import {
  Button,
  ButtonGroup,
  type ButtonGroupProps,
  type ButtonProps,
  SkeletonTextLines,
  TextButton,
  type TextButtonProps,
} from '../../../core-components';

type ElementAttributes = Omit<
  FormHTMLAttributes<HTMLFormElement>,
  'children' | 'onSubmit'
> &
  DataAttributes;

type FormState = {
  pending: boolean;
  disabled: boolean;
};

export type FormSubmitCallbacks = {
  resetForm: () => void;
  getCaptchaSolution: () => string | undefined;
  keepPending: () => void;
  keepDisabled: () => void;
};

export type FormProps = ElementAttributes & {
  fallback?: boolean;
  children?: ReactNode | ((state: FormState) => ReactNode);
  header?: HeaderSectionProps;
  meta?: ReactNode;
  footNote?: ReactNode;
  submitButton?: ButtonProps;
  cancelButton?: TextButtonProps;
  buttonGroup?: ButtonGroupProps;
  captcha?: FriendlyCaptchaProps;
  onSubmit?: (
    ev: FormEvent<HTMLFormElement>,
    callbacks: FormSubmitCallbacks,
  ) => Promise<void> | void;
};

export type FormRef = {
  resetForm: () => void;
};

export const Form = forwardRef(
  (
    {
      fallback,
      children,
      header,
      meta,
      footNote,
      submitButton,
      cancelButton,
      onSubmit,
      buttonGroup,
      captcha,
      ...attrs
    }: FormProps,
    ref,
  ) => {
    let [pending, setPending] = useState(false);
    let [keepPending, setKeepPending] = useState(false);
    let [keepDisabled, setKeepDisabled] = useState(false);
    let [captchaSolved, setCaptchaSolved] = useState(
      typeof captcha === 'undefined',
    );

    let captchaRef = useRef<FriendlyCaptchaRef>(null);

    let formState: FormState = {
      pending: keepPending || pending,
      disabled: keepDisabled || pending,
    };

    const resetForm = () => {
      captchaRef.current?.reset();
      setCaptchaSolved(typeof captcha === 'undefined');
      setPending(false);
    };

    useImperativeHandle(
      ref,
      () =>
        ({
          resetForm,
        }) satisfies FormRef,
    );

    let handleSubmit: FormEventHandler<HTMLFormElement> = async ev => {
      if (!onSubmit) {
        return;
      }

      try {
        setPending(true);

        await onSubmit(ev, {
          getCaptchaSolution: () => captchaRef.current?.getSolution(),
          resetForm,
          keepPending: () => {
            setKeepPending(true);
          },
          keepDisabled: () => {
            setKeepDisabled(true);
          },
        });
      } finally {
        setPending(false);
      }
    };

    let renderedChildren =
      typeof children === 'function' ? children(formState) : children;

    return (
      <form
        {...attrs}
        className={twMerge(attrs.className, 'space-y-8')}
        onSubmit={handleSubmit}
      >
        {header && <HeaderSection {...header} fallback={fallback} />}

        {renderedChildren && (
          <div className="space-y-8">{renderedChildren}</div>
        )}

        {captcha && (
          <FriendlyCaptcha
            {...captcha}
            ref={captchaRef}
            onSuccess={() => setCaptchaSolved(true)}
          />
        )}

        {fallback && <SkeletonTextLines number={2} className="h-1.5" />}
        {!fallback && meta && (
          <div className="info-md text-marine-high-emphasis">{meta}</div>
        )}

        {(fallback || submitButton || cancelButton || footNote) && (
          <div className="space-y-3">
            {fallback && (
              <ButtonGroup>
                <Button fallback />
                <Button fallback />
              </ButtonGroup>
            )}

            {!fallback && (submitButton || cancelButton) && (
              <ButtonGroup {...buttonGroup}>
                {submitButton && (
                  <Button
                    {...submitButton}
                    className="w-full sm:w-auto"
                    type="submit"
                    variant={submitButton.variant ?? 'primary'}
                    disabled={
                      submitButton.disabled ??
                      (keepDisabled || pending || !captchaSolved)
                    }
                    indicating={
                      submitButton.indicating ?? (keepPending || pending)
                    }
                  />
                )}

                {cancelButton && (
                  <div className="text-center">
                    <TextButton
                      {...cancelButton}
                      type="reset"
                      disabled={
                        cancelButton.disabled ?? (keepDisabled || pending)
                      }
                    />
                  </div>
                )}
              </ButtonGroup>
            )}

            {fallback && <SkeletonTextLines number={1} className="h-1.5" />}
            {!fallback && footNote && (
              <div className="info-md text-marine-high-emphasis">
                {footNote}
              </div>
            )}
          </div>
        )}
      </form>
    );
  },
);
Form.displayName = 'Form';
